import React from 'react';
import { useIntl } from 'react-intl';
import { Button, Grid, IconButton, LinearProgress, Typography } from '@mui/material';

import { AuthorizerContext } from 'app/providers/acl/AclProvider';
import { parseDate } from '../../Utils';
import useSettings from '../../hooks/useSettings';
import { getFileDataType, getFileSize, type MiddlewhereFile } from '../FilesUtils';
import ResponsiveDialog from '../../dialogs/ResponsiveDialog';
import FilesViewer from '../../files-viewer/FilesViewer';
import Icon from '../../Icon';
import FileExplorerScrollToTopButton from './FilesExplorerScrollToTopButton';

interface FilesExplorerFileDialogProps {
	open: boolean;
	onClose: VoidFunction;
	basePath: string;
	selectedFile: MiddlewhereFile;
	selectedFileContent: string;
	isLoadingFile: boolean;
	handleDeleteFile?: React.MouseEventHandler<HTMLButtonElement>;
	disableDelete?: boolean;
	disableDownload?: boolean;
}

export default function FilesExplorerFileDialog(props: FilesExplorerFileDialogProps) {
	const { t } = useSettings();
	const intl = useIntl();
	const authorizer = React.useContext(AuthorizerContext);
	const canDelete = authorizer.can('delete', 'documents');

	const scrollToRef = React.useRef<HTMLDivElement>(null);
	const dialogRef = React.useRef<HTMLDivElement>(null);

	return (
		<ResponsiveDialog
			open={props.open}
			onClose={props.onClose}
			maxWidth="md"
			title={
				<>
					{/* CLOSE BUTTON */}
					<IconButton className="absolute right-16 top-16" onClick={props.onClose}>
						<Icon icon="close" />
					</IconButton>
					<Grid ref={scrollToRef} container direction="column" spacing={1}>
						<Grid>
							{/* TITLE */}
							<Typography variant="h6">{props.selectedFile.name}</Typography>
						</Grid>
						<Grid>
							{/* INFO */}
							<Typography variant="subtitle2">
								{intl.formatMessage(
									{
										id: 'filesExplorerFileDialog.subtitle',
										defaultMessage: t('FILE_EXPLORER_FILE_DIALOG_SUBTITLE')
									},
									{
										size: getFileSize(props.selectedFile),
										date: intl.formatDate(parseDate(props.selectedFile.dateModified)?.toDate(), {
											year: 'numeric',
											month: '2-digit',
											day: '2-digit',
											hour: '2-digit',
											minute: '2-digit'
										})
									}
								)}
							</Typography>
						</Grid>
						<Grid>
							{/* DOWNLOAD BUTTON */}
							{!props.disableDownload && (
								<Button
									startIcon={<Icon icon="download" />}
									LinkComponent="a"
									href={`data:${getFileDataType(props.selectedFile)};base64,${props.selectedFileContent}`}
									download={props.selectedFile.name}
								>
									{t('VIEW_ITEM_BUTTON_DOWNLOAD')}
								</Button>
							)}
							{/* DELETE BUTTON */}
							{canDelete && !props.disableDelete && !!props.handleDeleteFile && (
								<Button startIcon={<Icon icon="delete" />} onClick={props.handleDeleteFile}>
									{t('VIEW_ITEM_BUTTON_DELETE')}
								</Button>
							)}
						</Grid>
					</Grid>
				</>
			}
			content={
				<>
					{props.isLoadingFile ? (
						<LinearProgress />
					) : (
						<FilesViewer
							filePath={`${props.basePath}\\${props.selectedFile.name}`}
							fileEncoded={props.selectedFileContent}
							fileType={props.selectedFile.name.split('.').slice(-1)[0]}
						/>
					)}
					<FileExplorerScrollToTopButton scrollToRef={scrollToRef} scrollContainerRef={dialogRef} />
				</>
			}
			slotProps={{ paper: { ref: dialogRef } }}
			scroll="body"
		/>
	);
}
